/* =========================================================
   AF — Shopping Summary (PDP)
   File: assets/af-shopping-summary.js
   - Reads cart via /cart.js
   - Reads selected variant + qty (best visible qty input)
   - Loads promos from assets/af-promos.json
   - Offers never stack (UI assumes non-stack)
   ========================================================= */

(function () {
  const $all = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function moneyBDTFromCents(cents, currencyCode) {
    const value = Math.round((cents || 0) / 100);
    const n = new Intl.NumberFormat("en-US").format(value);
    // PDP-20 / CART-07: match the ৳-symbol format used everywhere else on the
    // page (main price via snippets/price.liquid, courier-fee line) instead
    // of "BDT 374". Plain text — some call sites set it via textContent.
    return `৳${n}`;
  }

  function nowISO() {
    return new Date().toISOString();
  }

  function promoIsActive(p, now) {
    if (!p || p.active === false) return false;
    if (p.starts_at && now < p.starts_at) return false;
    if (p.ends_at && now > p.ends_at) return false;
    return true;
  }

  function calcSavingsCents(discount, subtotalCents) {
    if (!discount || !discount.kind) return 0;

    const maxCents = discount.max != null ? Math.round(Number(discount.max) * 100) : null;

    let s = 0;
    if (discount.kind === "fixed") {
      s = Math.round((discount.value || 0) * 100);
    } else if (discount.kind === "percent") {
      const pct = Number(discount.value || 0) / 100;
      s = Math.round(subtotalCents * pct);
    } else {
      return 0;
    }

    if (maxCents != null) s = Math.min(s, maxCents);
    return Math.max(0, Math.min(s, subtotalCents));
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart_fetch_failed");
    return res.json();
  }

  let _promosCache = null;
  let _promosCacheTime = 0;
  const PROMOS_TTL = 5 * 60 * 1000; // 5 minutes

  async function fetchPromos(url) {
    const now = Date.now();
    if (_promosCache && (now - _promosCacheTime) < PROMOS_TTL) return _promosCache;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error("promos_fetch_failed");
    const data = await res.json();
    _promosCache = data;
    _promosCacheTime = now;
    return data;
  }

  function findClosestProductForm(root) {
    const sectionId = root.getAttribute("data-section-id");
    if (sectionId) {
      const scoped =
        root.closest(`[data-section-id="${CSS.escape(sectionId)}"]`) ||
        root.closest(".section");
      if (scoped) {
        const f =
          scoped.querySelector('form[action*="/cart/add"]') ||
          scoped.querySelector("product-form form");
        if (f) return f;
      }
    }
    return (
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector("product-form form")
    );
  }

  function getSelectedVariantId(formEl) {
    if (!formEl) return null;
    const idInput = formEl.querySelector('input[name="id"]');
    return idInput ? idInput.value : null;
  }

  function isVisible(el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const hasBox = r && (r.width > 0 || r.height > 0);
    const ariaHidden = el.closest('[aria-hidden="true"]');
    const hiddenAttr = el.closest("[hidden]");
    return !ariaHidden && !hiddenAttr && hasBox;
  }

  function findBestQtyInput(root, formEl) {
    const productInfo = root.closest("product-info") || document.querySelector("product-info");

    const scopes = [];
    if (productInfo) scopes.push(productInfo);
    const section = root.closest(".section") || root.closest("[data-section-id]") || null;
    if (section) scopes.push(section);
    if (formEl) scopes.push(formEl);
    scopes.push(document);

    for (const scope of scopes) {
      const inputs = $all('input[name="quantity"]', scope);
      const visible = inputs.find(isVisible);
      if (visible) return visible;
      if (inputs[0]) return inputs[0];
    }
    return null;
  }

  function getSelectedQty(root, formEl) {
    const qtyInput = findBestQtyInput(root, formEl);
    const v = qtyInput ? parseInt(qtyInput.value || "1", 10) : 1;
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  function parseVariants(root) {
    const el = root.querySelector(".af-ss__variants-json");
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || "[]") || [];
    } catch {
      return [];
    }
  }

  function getVariantObj(variants, variantId) {
    if (!variantId) return null;
    return variants.find((x) => String(x.id) === String(variantId)) || null;
  }

  function getVariantPriceCents(variants, variantId) {
    const v = getVariantObj(variants, variantId);
    return v ? Number(v.price || 0) : 0;
  }

  // PDP-18: never project "after adding" savings for a variant (or product) that can't be bought.
  function isSelectedAvailable(variants, v) {
    if (v) return v.available !== false;
    if (!Array.isArray(variants) || variants.length === 0) return true;
    return variants.some((x) => x.available);
  }

  function setSoldOutState(root, soldOut) {
    root.classList.toggle("af-ss--sold-out", soldOut);

    const afterLabelEl = root.querySelector('[data-af-ss="afterLabel"]');
    const afterRow = afterLabelEl ? afterLabelEl.closest(".af-ss__row") : null;
    const promoTitle = root.querySelector(".af-ss__promo-title");
    const promosHost = root.querySelector('[data-af-ss="promos"]');
    const note = root.querySelector(".af-ss__note");

    [afterRow, promoTitle, promosHost, note].forEach((el) => {
      if (el) el.hidden = soldOut;
    });
  }

  function setText(root, key, value) {
    const el = root.querySelector(`[data-af-ss="${key}"]`);
    if (el) el.textContent = value;
  }

  function renderPromos(root, promos, currencyCode, projectedSubtotalCents) {
    const host = root.querySelector('[data-af-ss="promos"]');
    if (!host) return;

    if (!Array.isArray(promos) || promos.length === 0) {
      host.innerHTML = `<div class="af-ss__promo-muted">No offers configured.</div>`;
      return;
    }

    const now = nowISO();
    const active = promos.filter((p) => promoIsActive(p, now));

    if (active.length === 0) {
      host.innerHTML = `<div class="af-ss__promo-muted">No active offers right now.</div>`;
      return;
    }

    // Decide "Best" among unlocked offers only (highest estimated savings)
    const enriched = active.map((p) => {
      const minCents = Math.round(Number(p.min_subtotal || 0) * 100);
      const unlocked = projectedSubtotalCents >= minCents;
      const leftCents = Math.max(0, minCents - projectedSubtotalCents);
      const savingsCents = unlocked ? calcSavingsCents(p.discount, projectedSubtotalCents) : 0;
      const withOfferCents = unlocked ? Math.max(0, projectedSubtotalCents - savingsCents) : null;
      return { p, minCents, unlocked, leftCents, savingsCents, withOfferCents };
    });

    const best = enriched
      .filter((x) => x.unlocked)
      .sort((a, b) => (b.savingsCents || 0) - (a.savingsCents || 0))[0];

    host.innerHTML = enriched
      .map(({ p, unlocked, leftCents, savingsCents, withOfferCents }) => {
        const badge =
          p.type === "automatic"
            ? `<span class="af-ss__pill">Auto</span>`
            : `<span class="af-ss__pill">Code</span>`;

        const bestPill =
          best && best.p === p ? `<span class="af-ss__pill af-ss__pill--best">Best</span>` : ``;

        const code =
          p.type === "code" && p.code
            ? `<div class="af-ss__code">${p.code}</div>`
            : ``; // (no repeated “applied…” line here)

        const savingsLine =
          unlocked && savingsCents > 0
            ? `<div class="af-ss__sub">You save ~ <strong>${moneyBDTFromCents(
                savingsCents,
                currencyCode
              )}</strong></div>`
            : ``;

        const withOfferLine =
          unlocked && withOfferCents !== null
            ? `<div class="af-ss__with">With this offer: <strong>${moneyBDTFromCents(
                withOfferCents,
                currencyCode
              )}</strong></div>`
            : ``;

        // ✅ Unlocked = show wavy ribbon, remove "Unlocked" text line
        const statusLine = unlocked
          ? ``
          : `<div class="af-ss__status">Add <strong>${moneyBDTFromCents(
              leftCents,
              currencyCode
            )}</strong> more to unlock</div>`;

        const ribbon = unlocked
          ? `<div class="af-ss__ribbon af-ss__ribbon--ok"><span>Unlocked</span></div>`
          : ``;

        return `
          <div class="af-ss__promo">
            ${ribbon}
            <div class="af-ss__promo-top">
              <div class="af-ss__promo-name">${p.title || "Offer"} ${badge} ${bestPill}</div>
            </div>

            ${code}

            ${statusLine}
            ${withOfferLine}
            ${savingsLine}
          </div>
        `;
      })
      .join("");
  }

  async function refreshOne(root) {
    const currencyCode = root.getAttribute("data-currency-code") || "BDT";
    const promosUrl = root.getAttribute("data-promos-url");

    const variants = parseVariants(root);
    const formEl = findClosestProductForm(root);

    const cart = await fetchCart();
    const cartSubtotalCents = Number(cart.items_subtotal_price || cart.total_price || 0);

    const variantId = getSelectedVariantId(formEl);
    const qty = getSelectedQty(root, formEl);

    const productTitle = root.getAttribute("data-product-title") || "this item";
    const v = getVariantObj(variants, variantId);

    const soldOut = !isSelectedAvailable(variants, v);
    setSoldOutState(root, soldOut);

    let variantTitle = v ? (v.public_title || v.title || "") : "";
    if (variantTitle === "Default Title") variantTitle = "";

    const afterLabel = variantTitle
      ? `After adding: ${qty}x ${variantTitle} of ${productTitle}`
      : `After adding: ${qty}x ${productTitle}`;

    setText(root, "afterLabel", afterLabel);

    const variantPriceCents = getVariantPriceCents(variants, variantId);
    const projectedSubtotalCents = cartSubtotalCents + variantPriceCents * qty;

    setText(root, "cartSubtotal", moneyBDTFromCents(cartSubtotalCents, currencyCode));
    setText(root, "projectedSubtotal", moneyBDTFromCents(projectedSubtotalCents, currencyCode));

    // Sold out: the "after adding" row and offers are hidden above, so skip the
    // projection/promos work entirely — nothing to render, one fewer fetch.
    if (promosUrl && !soldOut) {
      try {
        const promosData = await fetchPromos(promosUrl);
        const promos = promosData && Array.isArray(promosData.promos) ? promosData.promos : [];
        renderPromos(root, promos, currencyCode, projectedSubtotalCents);
      } catch {
        const host = root.querySelector('[data-af-ss="promos"]');
        if (host) host.innerHTML = `<div class="af-ss__promo-muted">Could not load offers.</div>`;
      }
    }
  }

  function attachListeners(root) {
    const formEl = findClosestProductForm(root);
    const productInfo = root.closest("product-info") || document.querySelector("product-info");

    const schedule = () => {
      clearTimeout(root.__afSSTimer);
      root.__afSSTimer = setTimeout(() => {
        refreshOne(root).catch(() => {});
      }, 80);
    };

    if (formEl) {
      formEl.addEventListener("change", schedule, true);
      formEl.addEventListener("input", schedule, true);
    }

    if (productInfo) {
      productInfo.addEventListener("variant:change", schedule);
      productInfo.addEventListener("variantChange", schedule);
    }

    // Quantity +/- anywhere in same section triggers refresh
    document.addEventListener(
      "click",
      (e) => {
        const btn =
          e.target &&
          e.target.closest &&
          e.target.closest("quantity-input .quantity__button, .quantity__button");
        if (!btn) return;

        const btnScope =
          btn.closest("product-info") || btn.closest("[data-section-id]") || btn.closest(".section");
        const rootScope =
          root.closest("product-info") || root.closest("[data-section-id]") || root.closest(".section");

        if (btnScope && rootScope && btnScope !== rootScope) return;
        schedule();
      },
      true
    );
  }

  function init() {
    const blocks = $all(".af-ss");
    blocks.forEach((root) => {
      attachListeners(root);
      refreshOne(root).catch(() => {});
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
